import { useTheme } from "../theme/ThemeProvider";
import { shotSrc } from "../data/shots";
import DeviceIphone from "./DeviceIphone";

/**
 * One app screen, on the glass of an iPhone.
 *
 * The capture set exists in both appearances, so the screenshot follows the
 * page's own theme by default. Flip the site to light and every phone on it is
 * running the light build — which is the honest thing to show, and the reason
 * both halves of the set were captured.
 *
 * `appearance` overrides that. A `.stage` band stays dark while the rest of
 * the page is light, and a light screenshot inside one would be the only
 * white rectangle in an otherwise dark act.
 */
export default function Screen({
  slug,
  alt,
  className = "",
  eager = false,
  appearance,
  ...device
}) {
  const { theme } = useTheme();
  const look = appearance || theme;

  return (
    <DeviceIphone className={className} {...device}>
      <ShotImage slug={slug} alt={alt} appearance={look} eager={eager} />
    </DeviceIphone>
  );
}

/**
 * The bare `<picture>`, without a frame around it. Exported because the
 * showcase stacks six of these inside one shared frame and cross-fades them,
 * which it cannot do if each one brings its own phone.
 */
export function ShotImage({ slug, alt, appearance, eager = false }) {
  return (
    <picture>
      <source srcSet={shotSrc(slug, appearance, "webp")} type="image/webp" />
      <img
        /* Keyed on appearance so the browser swaps the decoded image rather
           than keeping the previous one until the new one loads. */
        key={appearance}
        /*
         * Before src, always. React sets attributes in the order written, and
         * an img that gets its src before loading="lazy" has already started
         * downloading: every screenshot on the page fetched at once and the
         * hero waited behind them.
         */
        loading={eager ? "eager" : "lazy"}
        // An eager screen is a first-screen screen: fetch it before the rest.
        fetchPriority={eager ? "high" : undefined}
        decoding={eager ? "sync" : "async"}
        src={shotSrc(slug, appearance, "png")}
        alt={alt}
        width="660"
        height="1434"
        draggable="false"
      />
    </picture>
  );
}
